import JsSIP from 'jssip'
import type { RTCSession, EndEvent, PeerConnectionEvent, SDPEvent } from 'jssip/lib/RTCSession'
import type { Call } from '../models/call'
import { makeIdleCall } from '../models/call'
import type { SipConfig } from '../models/call_state'
import { preferOpusAudio } from '../utils/sdp_audio'
import { CLARITY, clampSpeakerVolume, needsWebAudio } from '../utils/remote_audio'
import { withInputDevice, loadInputDeviceId, loadOutputDeviceId } from '@/shared/audio/audio_devices'
import { Ringtone, loadRingVolume } from '@/shared/audio/ringtone'

/** Élément/contexte audio étendu avec setSinkId (routage vers un haut-parleur
 *  précis) — pas encore typé dans la lib DOM pour AudioContext. */
type SinkSettable = { setSinkId?: (id: string) => Promise<void> }

JsSIP.debug.enable('JsSIP:*')

type CallChangeCallback = (call: Call) => void
type SipStateCallback = (state: SipState) => void

// JsSIP émet `newRTCSession` sur l'UA mais ce type n'est pas exporté
// directement — on le déclare localement avec uniquement le champ qu'on lit.
interface NewRTCSessionEvent {
  session: RTCSession
  originator: 'local' | 'remote'
}

/** États SessionStatus de JsSIP — copiés pour éviter le couplage à
 *  l'enum non-exporté côté CJS. */
const STATE_INVITE_RECEIVED = 3
const STATE_WAITING_FOR_ANSWER = 4
const ANSWERABLE_STATES = new Set([STATE_INVITE_RECEIVED, STATE_WAITING_FOR_ANSWER])

/** Statut SIP global (registration + connexion WS). */
export type SipState =
  | 'idle'         // pas démarré
  | 'connecting'   // WebSocket en cours
  | 'connected'    // WebSocket OK, registration en cours
  | 'registered'   // prêt à appeler
  | 'registration_failed'  // creds SIP refusés par Asterisk
  | 'disconnected' // WebSocket coupé

/** Erreurs catégorisées que le SipController peut signaler. Permet à l'UI
 *  d'afficher un message clair à l'agent (pas juste "Échec"). */
export type SipError =
  | { kind: 'media_denied'; message: string }
  | { kind: 'media_unavailable'; message: string }
  | { kind: 'no_ua'; message: string }
  | { kind: 'not_registered'; message: string }
  | { kind: 'call_failed'; cause?: string; message: string }
  // Info NON fatale : évènement transitoire (ex. reconnexion réseau en cours)
  // que l'UI peut afficher discrètement. N'interrompt pas l'appel. Les
  // consommateurs qui ne la gèrent pas l'ignorent simplement (pas de crash).
  | { kind: 'info'; message: string }

/**
 * Code du noise gate « proximité » exécuté dans un AudioWorklet.
 *
 * CRUCIAL : il tourne sur le THREAD AUDIO, pas le thread principal. L'ancienne
 * implémentation (ScriptProcessorNode) traitait l'audio sur le thread principal
 * → le moindre à-coup de l'UI (vumètre 60 fps, rendu Vue) faisait « bégayer »
 * le traitement → artefacts métalliques / réverbération sur la voix transmise.
 * Le worklet est immunisé contre ces à-coups → voix propre.
 *
 * Logique (par bloc de 128 échantillons) : RMS → ouvre le gate si le niveau
 * dépasse `openDb` (voix proche/forte), maintien 250 ms après le dernier
 * dépassement (ne hache pas entre les mots), gain lissé attaque/relâche.
 * Reçoit `openDb` et renvoie le niveau/état (~21 ms) au thread principal pour
 * le vumètre, via le `port`.
 */
const GATE_WORKLET_CODE = `
class MicGateProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.openDb = -38;
    this.gateGain = 0;
    this.holdSamples = 0;
    this.holdLen = Math.floor(0.25 * sampleRate);
    this.attackCoeff = 1 - Math.exp(-1 / (0.005 * sampleRate));
    this.releaseCoeff = 1 - Math.exp(-1 / (0.08 * sampleRate));
    this.frames = 0;
    this.port.onmessage = (e) => {
      if (e.data && typeof e.data.openDb === 'number') this.openDb = e.data.openDb;
    };
  }
  process(inputs, outputs) {
    const input = inputs[0];
    const output = outputs[0];
    if (!input || input.length === 0 || !output || output.length === 0) return true;
    const inp = input[0];
    const out = output[0];
    if (!inp) return true;
    const n = inp.length;
    let sum = 0;
    for (let i = 0; i < n; i++) sum += inp[i] * inp[i];
    const rms = Math.sqrt(sum / n);
    const db = 20 * Math.log10(rms + 1e-9);
    if (db >= this.openDb) this.holdSamples = this.holdLen;
    else this.holdSamples = Math.max(0, this.holdSamples - n);
    const target = this.holdSamples > 0 ? 1 : 0;
    let g = this.gateGain;
    for (let i = 0; i < n; i++) {
      const coeff = target > g ? this.attackCoeff : this.releaseCoeff;
      g += (target - g) * coeff;
      out[i] = inp[i] * g;
    }
    this.gateGain = g;
    this.frames += n;
    if (this.frames >= 1024) {
      this.frames = 0;
      this.port.postMessage({ db: db, open: target === 1 });
    }
    return true;
  }
}
registerProcessor('mic-gate', MicGateProcessor);
`

let _gateWorkletUrl: string | null = null
/** URL (Blob) du module worklet, créée une seule fois et réutilisée. */
function gateWorkletUrl(): string {
  if (!_gateWorkletUrl) {
    const blob = new Blob([GATE_WORKLET_CODE], { type: 'application/javascript' })
    _gateWorkletUrl = URL.createObjectURL(blob)
  }
  return _gateWorkletUrl
}

/**
 * Niveaux de filtrage du bruit micro, du plus NATUREL (clarté max, peu de
 * traitement) au plus AGRESSIF (anti-bruit fort, voix un peu « lissée »).
 *
 * L'agent dose lui-même clarté ↔ anti-bruit avec un slider, EN DIRECT pendant
 * l'appel (cf. setMicDenoiseLevel → applyConstraints). C'est LE compromis qu'on
 * ne pouvait pas régler à l'aveugle : seul l'agent entend le résultat.
 *
 * `autoGainControl` reste à false partout : le gain auto remonterait la pièce
 * quand l'agent se tait → défait le rejet de proximité du noise gate.
 */
const DENOISE_LEVELS: MediaTrackConstraints[] = [
  // 0 — Clarté max : voix la plus naturelle (juste l'anti-écho).
  { echoCancellation: true, noiseSuppression: false, autoGainControl: false },
  // 1 — Doux : anti-bruit standard.
  { echoCancellation: true, noiseSuppression: true, autoGainControl: false },
  // 2 — Fort : + focus voix ML.
  {
    echoCancellation: true, noiseSuppression: true, autoGainControl: false,
    ...({ voiceIsolation: true } as Record<string, boolean>),
  },
  // 3 — Anti-bruit max : + filtres goog renforcés (passe-haut, NS2, frappe…).
  {
    echoCancellation: true, noiseSuppression: true, autoGainControl: false,
    ...({
      voiceIsolation: true,
      googNoiseSuppression: true,
      googNoiseSuppression2: true,
      googHighpassFilter: true,
      googTypingNoiseDetection: true,
      googExperimentalNoiseSuppression: true,
    } as Record<string, boolean>),
  },
]
const DENOISE_DEFAULT = 1  // « Doux » : réglage validé (clair + sans bruit).

/**
 * Controller MVC strict côté front : encapsule toute la logique SIP.
 * Les composants Vue ne savent rien de JsSIP.
 */
export class SipController {
  private ua: JsSIP.UA | null = null
  private session: RTCSession | null = null
  private current: Call = makeIdleCall()
  private listeners: CallChangeCallback[] = []
  private stateListeners: SipStateCallback[] = []
  private errorListeners: ((err: SipError) => void)[] = []
  private audioEl: HTMLAudioElement | null = null
  private _state: SipState = 'idle'

  // ── Micro : traitement du signal + gain réglable par l'agent ─────────────
  // On capte NOUS-MÊMES le micro (anti-bruit / anti-écho / gain auto activés)
  // et on le fait passer par un GainNode avant de le transmettre à JsSIP
  // (option `mediaStream`). Deux corrections : (1) le correspondant n'entend
  // plus le bruit de fond ; (2) l'agent règle le volume de SA voix en direct.
  private micGain = SipController.loadMicGain()
  private micAudioCtx: AudioContext | null = null
  private micRawStream: MediaStream | null = null
  // Filet contre la panne SILENCIEUSE : un AudioContext suspendu en cours
  // d'appel (bascule de périphérique, casque Bluetooth, mise en veille) cesse
  // de produire. Côté micro le correspondant n'entend plus rien ; côté écoute
  // c'est l'agent, car l'élément <audio> est volontairement coupé quand le
  // graphe est actif. Dans les deux cas l'appel reste établi et RIEN ne
  // remonte dans les journaux — d'où cette surveillance.
  private audioWatchdog: ReturnType<typeof setInterval> | null = null
  private micGainNode: GainNode | null = null
  // Noise gate « proximité » : ne laisse passer le micro que quand le niveau
  // dépasse `micGateOpenDb` (la voix proche du casque est forte ; une personne
  // à côté est bien plus faible → restée sous le seuil → coupée). Le nœud
  // mesure et applique le gate dans le thread audio.
  private micGateNode: AudioWorkletNode | null = null
  private micGateOpenDb = SipController.loadGateDb()
  // Niveau de filtrage du bruit, 0 (clarté) → 3 (anti-bruit max) (cf. DENOISE_LEVELS).
  private micDenoiseLevel = SipController.loadDenoiseLevel()
  // Télémétrie temps réel du gate (pour le vumètre de réglage UI).
  private _micLevelDb = -99   // niveau RMS courant du micro (dBFS)
  private _gateOpen = false    // le gate laisse-t-il passer en ce moment ?
  private micTestActive = false  // graphe ouvert pour régler le micro hors appel

  // ── Haut-parleur : voix ENTRANTE (le correspondant) ──────────────────────
  // Volume réglable (avec boost > 100 %) + « clarté » optionnelle (passe-haut +
  // compresseur léger). OPT-IN : volume 100 % + clarté coupée ⇒ lecture directe
  // via l'élément <audio> (identique au comportement actuel). Le graphe WebAudio
  // n'est construit QUE si un boost (>1) ou la clarté est demandé.
  private speakerVolume = SipController.loadSpeakerVolume()
  private clarityOn = SipController.loadClarity()
  private remoteCtx: AudioContext | null = null
  private remoteGainNode: GainNode | null = null
  private remoteGraphStreamId: string | null = null  // stream traité (anti-rebuild)

  // ── Périphériques choisis (page Paramètres) ──────────────────────────────
  // Le choix (micro/haut-parleur) est écrit dans localStorage par la PAGE
  // PARAMÈTRES, un autre composant. Ce contrôleur est un singleton longue durée :
  // on RELIT donc le choix depuis localStorage À CHAQUE usage (capture micro /
  // routage haut-parleur), jamais en cache à la construction, sinon un changement
  // fait après le démarrage ne serait jamais pris en compte. '' = défaut système.

  private static loadSpeakerVolume(): number {
    try {
      const v = Number(localStorage.getItem('pexitalk:speakerVol'))
      if (Number.isFinite(v)) return clampSpeakerVolume(v)
    } catch { /* localStorage indisponible */ }
    return 1
  }

  private static loadClarity(): boolean {
    try { return localStorage.getItem('pexitalk:speakerClarity') === '1' }
    catch { return false }
  }

  private static loadDenoiseLevel(): number {
    try {
      const v = Number(localStorage.getItem('pexitalk:micDenoise'))
      if (Number.isInteger(v) && v >= 0 && v < DENOISE_LEVELS.length) return v
    } catch { /* localStorage indisponible */ }
    return DENOISE_DEFAULT
  }

  private static loadMicGain(): number {
    try {
      const v = Number(localStorage.getItem('pexitalk:micGain'))
      if (Number.isFinite(v) && v >= 0) return Math.min(2, v)
    } catch { /* localStorage indisponible */ }
    return 1
  }

  // Seuil d'ouverture du gate en dBFS (négatif). Plus HAUT (ex. -30) = plus
  // strict : il faut parler fort/près pour passer → coupe mieux les voisins.
  // Plus BAS (ex. -50) = plus permissif. Réglable sans rebuild via
  // localStorage('pexitalk:micGateDb'). Défaut -38 : coupe une voix à ~1 m
  // tout en laissant passer l'agent au casque.
  private static loadGateDb(): number {
    try {
      const v = Number(localStorage.getItem('pexitalk:micGateDb'))
      if (Number.isFinite(v) && v < 0 && v > -90) return v
    } catch { /* localStorage indisponible */ }
    return -38
  }

  // Ringback (tonalité de retour d'appel) joué LOCALEMENT pendant que ça
  // sonne. On ne compte pas sur l'early-media de l'opérateur : en WebRTC le
  // canal DTLS-SRTP n'est souvent établi qu'au décroché → l'agent
  // n'entendrait rien pendant l'attente. On synthétise donc la tonalité.
  // Sonnerie d'appel ENTRANT (le retour d'appel ci-dessous est son pendant
  // SORTANT). Les deux sont mutuellement exclusifs.
  private ringtone = new Ringtone()
  private ringbackCtx: AudioContext | null = null
  private ringbackOsc: OscillatorNode | null = null
  private ringbackGain: GainNode | null = null
  private ringbackTimer: ReturnType<typeof setInterval> | null = null

  // ── Résilience ICE : reprise auto quand le réseau blipe ───────────────────
  // Un micro-décrochage réseau fait passer ICE 'disconnected' puis 'failed'.
  // Sans reprise, l'agent perd le son pour TOUT le reste de l'appel. On relance
  // alors une négociation avec ICE restart (nouveaux candidats) via JsSIP.
  //  - grace timer : on laisse ~2 s à 'disconnected' pour se rétablir seul
  //    (blip très bref) avant de forcer un restart ; annulé si ça reconnecte.
  //  - anti-boucle : max ICE_MAX_ATTEMPTS restarts dans une fenêtre glissante,
  //    avec petit back-off ; au-delà on cesse d'essayer (erreur non fatale).
  private static readonly ICE_GRACE_MS = 2000
  private static readonly ICE_MAX_ATTEMPTS = 3
  private static readonly ICE_ATTEMPT_WINDOW_MS = 30000
  // Balayage des graphes WebAudio pendant l'appel.
  private static readonly AUDIO_WATCHDOG_MS = 4000
  // Au-delà, une reprise ICE est considérée comme sans effet et le verrou est
  // relâché pour qu'une nouvelle tentative reste possible.
  private static readonly ICE_RECOVER_TIMEOUT_MS = 12000
  private iceGraceTimer: ReturnType<typeof setTimeout> | null = null
  private iceRestartTimer: ReturnType<typeof setTimeout> | null = null
  private iceRestartAttempts: number[] = []  // timestamps des tentatives récentes
  private iceRecovering = false               // une reprise est-elle en cours ?
  private iceUnlockTimer: ReturnType<typeof setTimeout> | null = null

  /** Config ICE.
   *
   *  Par défaut : **aucun serveur STUN/TURN** → on ne rassemble que les
   *  candidats `host`. JsSIP n'émet l'INVITE qu'une fois le rassemblement ICE
   *  TERMINÉ ; avec un STUN, Chrome interroge stun.l.google.com sur TOUTES les
   *  interfaces (Wi-Fi, Ethernet, VPN, Docker…) et attend leurs réponses —
   *  10–40 s de « Connexion… » avant même la sonnerie quand une interface est
   *  morte. Les candidats `host` se rassemblent instantanément, donc l'INVITE
   *  part tout de suite.
   *
   *  L'audio reste fonctionnel à travers le NAT car Asterisk fait du RTP
   *  symétrique (`rtp_symmetric=yes`, `rewrite_contact`, `force_rport`,
   *  `media_use_received_transport=yes`) : le client envoie le média vers l'IP
   *  publique d'Asterisk, qui répond sur le même chemin NAT. Pas besoin de STUN.
   *
   *  Un TURN ne devient utile que pour un client derrière un NAT très
   *  restrictif sans chemin média direct — on le brancherait alors via
   *  `SipConfig.iceServers` (override explicite ci-dessous).
   */
  private get pcConfig(): RTCConfiguration {
    return { iceServers: this.config.iceServers ?? [] }
  }

  constructor(private config: SipConfig) {}

  get state(): SipState {
    return this._state
  }

  /** S'abonner aux changements d'état du registre SIP (UI status indicator). */
  onStateChange(cb: SipStateCallback): () => void {
    this.stateListeners.push(cb)
    return () => {
      this.stateListeners = this.stateListeners.filter((l) => l !== cb)
    }
  }

  /** S'abonner aux erreurs catégorisées (toast UI, retry, etc.). */
  onError(cb: (err: SipError) => void): () => void {
    this.errorListeners.push(cb)
    return () => {
      this.errorListeners = this.errorListeners.filter((l) => l !== cb)
    }
  }

  private setState(next: SipState): void {
    if (this._state === next) return
    this._state = next
    console.log('[PexiTalk SIP] state →', next)
    this.stateListeners.forEach((cb) => cb(next))
  }

  private emitError(err: SipError): void {
    console.error('[PexiTalk SIP] error', err)
    this.errorListeners.forEach((cb) => cb(err))
  }

  private ensureAudioEl(): HTMLAudioElement {
    if (this.audioEl) return this.audioEl
    const el = document.createElement('audio')
    el.autoplay = true
    el.id = 'pexitalk-remote-audio'
    // En Electron, la permission audio est implicitement accordée.
    // Le `playsInline` aide en cas de fallback navigateur (mobile).
    el.setAttribute('playsinline', '')
    document.body.appendChild(el)
    this.audioEl = el
    this.applyOutputDevice()  // route vers le haut-parleur choisi (si défini)
    return el
  }

  /** Route la voix entrante vers le haut-parleur choisi (setSinkId), à la fois
   *  sur l'élément <audio> (chemin direct) ET le contexte WebAudio (chemin
   *  boost/clarté) — sinon le traitement ignorerait le choix. '' = défaut
   *  système. Best-effort : ignore proprement si setSinkId est indisponible ou
   *  l'id invalide (périphérique débranché). */
  private applyOutputDevice(): void {
    const id = loadOutputDeviceId()
    const el = this.audioEl as (HTMLAudioElement & SinkSettable) | null
    if (el && typeof el.setSinkId === 'function') {
      el.setSinkId(id).catch((e: unknown) => console.warn('[PexiTalk SIP] setSinkId élément', e))
    }
    const ctx = this.remoteCtx as (AudioContext & SinkSettable) | null
    if (ctx && typeof ctx.setSinkId === 'function') {
      ctx.setSinkId(id).catch((e: unknown) => console.warn('[PexiTalk SIP] setSinkId contexte', e))
    }
  }

  /** Attache la piste audio distante à notre élément `<audio>`. */
  private attachRemoteStream(): void {
    if (!this.session) return
    const pc = this.session.connection
    if (!pc) return
    const receivers = pc.getReceivers()
    const audioTracks = receivers
      .filter((r: RTCRtpReceiver) => r.track?.kind === 'audio')
      .map((r: RTCRtpReceiver) => r.track)
    console.log('[PexiTalk SIP] attachRemoteStream receivers:', receivers.length, 'audioTracks:', audioTracks.length)
    if (audioTracks.length === 0) return
    const audio = this.ensureAudioEl()
    const existing = audio.srcObject as MediaStream | null
    const existingIds = new Set(existing?.getAudioTracks().map((t) => t.id) ?? [])
    const sameTrack = audioTracks.every((t) => existingIds.has(t.id))
    if (!sameTrack || !existing) {
      audio.srcObject = new MediaStream(audioTracks)
    }
    // Applique volume / clarté (et bascule élément ↔ graphe WebAudio au besoin).
    this.applyRemoteProcessing()
    console.log('[PexiTalk SIP] audio paused:', audio.paused, 'readyState:', audio.readyState, 'volume:', audio.volume, 'muted:', audio.muted)
    audio.play().catch((e: Error) => {
      console.warn('[PexiTalk SIP] audio.play() blocked', e)
    })
  }

  /**
   * Applique le volume haut-parleur et la clarté à la voix entrante.
   *
   * Deux chemins :
   *  - CHEMIN SIMPLE (défaut) — pas de boost (≤ 1) et clarté coupée : on joue
   *    via l'élément <audio> et on règle `audio.volume`. Aucun WebAudio →
   *    strictement le comportement de production actuel quand rien n'est réglé.
   *  - CHEMIN TRAITÉ — boost (> 1) ou clarté : on route le flux distant dans un
   *    graphe WebAudio (passe-haut + compresseur léger + gain) vers les
   *    haut-parleurs, et on coupe l'élément pour ne pas jouer deux fois.
   *
   * Tout échec du graphe RETOMBE sur le chemin simple (jamais de silence).
   */
  private applyRemoteProcessing(): void {
    const audio = this.audioEl
    const stream = (audio?.srcObject as MediaStream | null) ?? null
    if (!audio) return
    const vol = clampSpeakerVolume(this.speakerVolume)

    if (!stream || !needsWebAudio(vol, this.clarityOn)) {
      this.teardownRemoteProcessing()
      audio.muted = false
      audio.volume = Math.min(1, vol)
      return
    }
    try {
      this.buildRemoteGraph(stream, vol)
      // Le son sort du graphe (ctx.destination) → on coupe l'élément.
      audio.muted = true
    } catch (e) {
      console.warn('[PexiTalk SIP] graphe haut-parleur indisponible, repli direct', e)
      this.teardownRemoteProcessing()
      audio.muted = false
      audio.volume = Math.min(1, vol)
    }
  }

  /** (Re)construit le graphe WebAudio de la voix entrante pour `stream`. */
  private buildRemoteGraph(stream: MediaStream, vol: number): void {
    // La clé inclut l'état de CLARTÉ : la basculer force une reconstruction.
    // Sans ça, activer la clarté alors qu'un graphe de boost existe déjà ne
    // ferait qu'ajuster le gain → le passe-haut/compresseur ne serait jamais
    // ajouté (ni retiré quand on la coupe).
    const key = stream.getAudioTracks().map((t) => t.id).join(',') + (this.clarityOn ? '|c' : '')
    if (this.remoteCtx && this.remoteGraphStreamId === key) {
      // Même flux ET même clarté : on ajuste juste le gain (volume en direct).
      if (this.remoteGainNode) this.remoteGainNode.gain.value = vol
      return
    }
    this.teardownRemoteProcessing()
    const Ctx = window.AudioContext
      || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const ctx = new Ctx()
    this.remoteCtx = ctx
    try { void ctx.resume() } catch { /* déjà actif */ }
    // L'élément <audio> est coupé tant que ce graphe est actif : s'il se
    // suspend, l'agent n'entend PLUS RIEN jusqu'au raccrochage.
    ctx.addEventListener('statechange', () => {
      this.reviveAudioContext(ctx, 'haut-parleur')
    })
    const source = ctx.createMediaStreamSource(stream)
    const gain = ctx.createGain()
    gain.gain.value = vol
    this.remoteGainNode = gain
    let node: AudioNode = source
    if (this.clarityOn) {
      const hp = ctx.createBiquadFilter()
      hp.type = 'highpass'
      hp.frequency.value = CLARITY.highpassHz
      const comp = ctx.createDynamicsCompressor()
      comp.threshold.value = CLARITY.compressor.threshold
      comp.knee.value = CLARITY.compressor.knee
      comp.ratio.value = CLARITY.compressor.ratio
      comp.attack.value = CLARITY.compressor.attack
      comp.release.value = CLARITY.compressor.release
      node.connect(hp)
      hp.connect(comp)
      node = comp
    }
    node.connect(gain)
    gain.connect(ctx.destination)
    this.applyOutputDevice()  // route le graphe vers le haut-parleur choisi
    this.remoteGraphStreamId = key
  }

  /** Libère le graphe WebAudio de la voix entrante (retour lecture élément). */
  private teardownRemoteProcessing(): void {
    try { this.remoteGainNode?.disconnect() } catch { /* ignore */ }
    try { void this.remoteCtx?.close() } catch { /* ignore */ }
    this.remoteCtx = null
    this.remoteGainNode = null
    this.remoteGraphStreamId = null
  }

  /** Volume haut-parleur de la voix entrante : 0 = muet, 1 = normal, 2 = ×2.
   *  Appliqué EN DIRECT et mémorisé. */
  setSpeakerVolume(ratio: number): void {
    this.speakerVolume = clampSpeakerVolume(ratio)
    try { localStorage.setItem('pexitalk:speakerVol', String(this.speakerVolume)) } catch { /* ignore */ }
    this.applyRemoteProcessing()
  }

  /** Volume haut-parleur courant (0..2). */
  getSpeakerVolume(): number { return this.speakerVolume }

  /** Active/coupe la « clarté » de la voix entrante (passe-haut + compresseur).
   *  Appliqué EN DIRECT et mémorisé. */
  setRemoteClarity(on: boolean): void {
    this.clarityOn = !!on
    try { localStorage.setItem('pexitalk:speakerClarity', on ? '1' : '0') } catch { /* ignore */ }
    this.applyRemoteProcessing()
  }

  /** La clarté de la voix entrante est-elle active ? */
  isRemoteClarity(): boolean { return this.clarityOn }

  /**
   * Demande la permission micro de manière proactive — avant tout appel.
   * Permet à l'agent de voir le prompt navigateur et d'autoriser AVANT
   * qu'un appel arrive (sinon la 1ère sonnerie est ratée le temps qu'il
   * réponde au prompt).
   *
   * Retourne `true` si l'accès est accordé.
   */
  async ensureMicrophoneAccess(): Promise<boolean> {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.emitError({
        kind: 'media_unavailable',
        message: "L'API micro n'est pas disponible. Vérifiez votre navigateur.",
      })
      return false
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true, video: false,
      })
      // On libère immédiatement — JsSIP recréera son propre stream lors
      // de l'appel. Ce check sert uniquement à vérifier la permission.
      stream.getTracks().forEach((t) => t.stop())
      return true
    } catch (e: any) {
      if (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError') {
        this.emitError({
          kind: 'media_denied',
          message: "Permission micro refusée. Autorisez l'accès depuis les paramètres.",
        })
      } else if (e?.name === 'NotFoundError' || e?.name === 'DevicesNotFoundError') {
        this.emitError({
          kind: 'media_unavailable',
          message: 'Aucun microphone détecté. Branchez un casque ou un micro.',
        })
      } else {
        this.emitError({
          kind: 'media_unavailable',
          message: `Erreur micro : ${e?.name || 'inconnue'}`,
        })
      }
      return false
    }
  }

  initialize(): void {
    if (this.ua) {
      console.warn('[PexiTalk SIP] already initialized')
      return
    }
    const wsPath = this.config.wsPath || '/ws'
    // Dev local : WS direct vers Asterisk (port 8088 exposé par compose).
    // Prod : WSS sur 443 — Caddy (sip.<domaine>) termine le TLS et forward
    // vers le 8088 interne, qui n'est jamais exposé par le firewall.
    const isLocal =
      this.config.host.startsWith('localhost') || this.config.host.startsWith('127.')
    const wsUrl = isLocal
      ? `ws://${this.config.host}:8088${wsPath}`
      : `wss://${this.config.host}${wsPath}`
    console.log('[PexiTalk SIP] connecting to', wsUrl, 'as', this.config.username)

    this.setState('connecting')
    const socket = new JsSIP.WebSocketInterface(wsUrl)
    this.ua = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${this.config.username}@${this.config.host}`,
      password: this.config.password,
      register: true,
      session_timers: false,
    })
    this.ua.on('connecting', () => this.setState('connecting'))
    this.ua.on('connected', () => {
      console.log('[PexiTalk SIP] WebSocket connected')
      this.setState('connected')
    })
    this.ua.on('disconnected', () => {
      console.warn('[PexiTalk SIP] WebSocket disconnected')
      this.setState('disconnected')
    })
    this.ua.on('registered', () => {
      console.log('[PexiTalk SIP] registered ✓')
      this.setState('registered')
    })
    this.ua.on('unregistered', () => {
      console.warn('[PexiTalk SIP] unregistered')
      // L'UA peut se réenregistrer automatiquement
      if (this._state === 'registered') this.setState('connected')
    })
    this.ua.on('registrationFailed', (e: { cause?: string }) => {
      console.error('[PexiTalk SIP] registration failed:', e?.cause)
      this.setState('registration_failed')
    })
    this.setupListeners()
    this.ua.start()
  }

  destroy(): void {
    this.stopLocalTones()
    this.resetIceRecovery()
    this.ua?.stop()
    this.ua = null
    this.session = null
    this.setState('idle')
  }

  /**
   * Lance un appel sortant. Pré-vérifie la registration et la permission
   * micro pour ne pas échouer silencieusement.
   *
   * Retourne `true` si l'INVITE est réellement parti — l'UI ne doit basculer
   * en mode appel que dans ce cas (sinon le panneau restait en « Connexion »
   * alors qu'aucun appel n'existait côté Asterisk).
   */
  async call(
    number: string,
    outboundCallerId?: string,
    outboundTrunk?: string,
  ): Promise<boolean> {
    if (!this.ua) {
      this.emitError({ kind: 'no_ua', message: 'Téléphone non initialisé.' })
      return false
    }
    if (this._state !== 'registered') {
      this.emitError({
        kind: 'not_registered',
        message:
          this._state === 'registration_failed'
            ? "Identifiants SIP incorrects (vérifiez les paramètres)."
            : 'Téléphone non enregistré sur le serveur — patientez quelques secondes.',
      })
      return false
    }
    const cleanNumber = number.replace(/[^\d*#+]/g, '')
    if (!cleanNumber) return false

    // Demande micro AVANT d'appeler JsSIP, pour qu'on puisse afficher
    // une erreur claire si refusée (sinon JsSIP throw une erreur générique).
    const ok = await this.ensureMicrophoneAccess()
    if (!ok) return false

    // Flux micro TRAITÉ (anti-bruit) + gain réglable, transmis tel quel à JsSIP.
    let localStream: MediaStream
    try {
      localStream = await this.buildLocalStream()
    } catch (e: any) {
      this.teardownLocalStream()
      this.emitError({
        kind: 'media_denied',
        message: `Micro indisponible : ${e?.name || 'erreur'}`,
      })
      return false
    }

    const extraHeaders: string[] = []
    const cid = (outboundCallerId || '').trim()
    if (cid) {
      const safeCid = cid.replace(/[\r\n]/g, '').slice(0, 32)
      extraHeaders.push(`X-Pexitalk-Caller-Id: ${safeCid}`)
    }
    // Trunk SIP résolu (user/équipe/campagne/global) : le dialplan le lit en
    // priorité avant de retomber sur le trunk global Asterisk.
    const trunk = (outboundTrunk || '').trim()
    if (trunk) {
      const safeTrunk = trunk.replace(/[^A-Za-z0-9_]/g, '').slice(0, 64)
      if (safeTrunk) extraHeaders.push(`X-Pexitalk-Trunk: ${safeTrunk}`)
    }
    try {
      this.session = this.ua.call(`sip:${cleanNumber}@${this.config.host}`, {
        mediaStream: localStream,
        pcConfig: this.pcConfig,
        rtcOfferConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false,
        },
        extraHeaders: extraHeaders.length ? extraHeaders : undefined,
      })
    } catch (e: any) {
      console.error('[PexiTalk SIP] call() failed', e)
      this.teardownLocalStream()
      this.session = null
      this.emitError({
        kind: 'call_failed',
        cause: e?.message,
        message: `Échec de l'appel : ${e?.message || 'inconnu'}`,
      })
      return false
    }
    this.update({
      id: crypto.randomUUID(),
      remoteNumber: cleanNumber,
      direction: 'outbound',
      startedAt: new Date(),
      durationSeconds: 0,
      status: 'connecting',
      muted: false,
      onHold: false,
    })
    this.attachSessionListeners()
    return true
  }

  hangup(): void {
    this.stopLocalTones()
    this.resetIceRecovery()
    const session = this.session
    let terminated = false
    if (session) {
      try {
        session.terminate()
        terminated = true
      } catch (e) {
        console.warn('[PexiTalk SIP] terminate a échoué', e)
      }
    } else {
      console.warn('[PexiTalk SIP] raccrochage sans session active')
    }
    // Sans ce filet, l'interface restait bloquée sur « appel en cours » et
    // l'agent n'avait d'autre issue que de RECHARGER LA PAGE. C'est le cas
    // après une coupure réseau : la session a disparu côté navigateur, ou
    // `terminate()` refuse d'agir, et l'ancien code se contentait alors de
    // journaliser un avertissement sans jamais libérer l'écran. Constaté en
    // production sur des canaux restés fantômes (oct. 2026).
    if (!terminated) this.releaseStuckCall()
  }

  /** Libère un appel devenu fantôme : plus de session exploitable côté
   *  navigateur alors que l'interface affiche encore une communication.
   *  Reproduit exactement le démontage d'une fin d'appel normale, pour que
   *  l'agent reprenne la main immédiatement. */
  private releaseStuckCall(): void {
    this.teardownLocalStream()
    this.teardownRemoteProcessing()
    this.session = null
    this.update({ ...this.current, status: 'ended', endedAt: new Date() })
    if (this.audioEl) this.audioEl.srcObject = null
  }

  /**
   * Décroche un appel entrant. Accepte les états INVITE_RECEIVED (3) ET
   * WAITING_FOR_ANSWER (4) — la version précédente ne tolérait que (4),
   * ce qui bloquait silencieusement le décrochage quand JsSIP n'avait pas
   * encore envoyé le 180 Ringing.
   */
  async answer(): Promise<void> {
    if (!this.session) {
      console.warn('[PexiTalk SIP] answer: no session')
      return
    }
    const status = this.session.status
    if (!ANSWERABLE_STATES.has(status)) {
      console.warn('[PexiTalk SIP] answer: session in non-answerable state', status)
      return
    }
    // Permission micro — sinon le browser bloque silencieusement le
    // getUserMedia interne de JsSIP et l'appel timeout côté distant.
    const ok = await this.ensureMicrophoneAccess()
    if (!ok) {
      // L'erreur a déjà été émise par ensureMicrophoneAccess. On raccroche
      // proprement pour ne pas laisser l'appel sonner dans le vide.
      try { this.session.terminate({ status_code: 486, reason_phrase: 'Busy Here' }) }
      catch { /* ignore */ }
      return
    }
    // Flux micro traité (anti-bruit) + gain réglable, comme en émission.
    let localStream: MediaStream
    try {
      localStream = await this.buildLocalStream()
    } catch (e: any) {
      this.teardownLocalStream()
      this.emitError({
        kind: 'media_denied',
        message: `Micro indisponible : ${e?.name || 'erreur'}`,
      })
      try { this.session.terminate({ status_code: 486, reason_phrase: 'Busy Here' }) }
      catch { /* ignore */ }
      return
    }
    try {
      this.session.answer({
        mediaStream: localStream,
        pcConfig: this.pcConfig,
        rtcAnswerConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false,
        },
      })
    } catch (e: any) {
      console.error('[PexiTalk SIP] answer() failed', e)
      this.teardownLocalStream()
      this.emitError({
        kind: 'call_failed',
        cause: e?.message,
        message: `Échec du décrochage : ${e?.message || 'inconnu'}`,
      })
    }
  }

  mute(): void {
    if (!this.session) return
    this.session.mute({ audio: true })
    this.update({ ...this.current, muted: true })
  }

  unmute(): void {
    if (!this.session) return
    this.session.unmute({ audio: true })
    this.update({ ...this.current, muted: false })
  }

  /** Contraintes de capture du niveau de filtrage courant (cf. DENOISE_LEVELS). */
  private _micConstraints(): MediaTrackConstraints {
    return DENOISE_LEVELS[this.micDenoiseLevel] ?? DENOISE_LEVELS[DENOISE_DEFAULT]
  }

  /** Niveau de filtrage du bruit, 0 (clarté max) → 3 (anti-bruit max). Appliqué
   *  EN DIRECT au micro de l'appel en cours (applyConstraints, sans recouper le
   *  micro) et mémorisé pour les appels suivants. */
  setMicDenoiseLevel(level: number): void {
    const l = Math.max(0, Math.min(DENOISE_LEVELS.length - 1, Math.round(level)))
    this.micDenoiseLevel = l
    try { localStorage.setItem('pexitalk:micDenoise', String(l)) } catch { /* ignore */ }
    const track = this.micRawStream?.getAudioTracks()[0]
    if (track) {
      track.applyConstraints(DENOISE_LEVELS[l]).catch((e: unknown) => {
        console.warn('[PexiTalk SIP] applyConstraints (denoise) a échoué', e)
      })
    }
  }

  /** Niveau de filtrage courant (0..3). */
  getMicDenoiseLevel(): number { return this.micDenoiseLevel }

  /** Capte le micro (DSP activé) puis le route via : noise gate → GainNode →
   *  flux transmis à JsSIP.
   *   - Le gate (cf. _buildGateNode) ne laisse passer que la voix proche du
   *     casque et coupe les autres locuteurs de la pièce.
   *   - Le GainNode règle le volume de la voix de l'agent EN DIRECT (setMicGain),
   *     APRÈS le gate pour que le réglage de volume ne décale pas le seuil. */
  /** Capture le micro avec le périphérique choisi + le niveau de denoise courant.
   *  ROBUSTESSE : si le micro choisi est débranché (`exact` → OverconstrainedError
   *  / NotFoundError), on RETENTE sur le micro par défaut système plutôt que de
   *  faire échouer l'appel. Les autres erreurs (permission refusée…) remontent. */
  private async _getMicStream(): Promise<MediaStream> {
    const base = this._micConstraints()
    const id = loadInputDeviceId()  // relu frais (cf. note champ périphériques)
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: withInputDevice(base, id), video: false,
      })
    } catch (e: any) {
      if (id && (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError')) {
        console.warn('[PexiTalk SIP] micro choisi indisponible → repli défaut', e?.name)
        return await navigator.mediaDevices.getUserMedia({ audio: base, video: false })
      }
      throw e
    }
  }

  private async buildLocalStream(): Promise<MediaStream> {
    const raw = await this._getMicStream()
    this.micRawStream = raw
    const Ctx = window.AudioContext
      || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const ctx = new Ctx()
    this.micAudioCtx = ctx
    try { await ctx.resume() } catch { /* déjà actif */ }
    const source = ctx.createMediaStreamSource(raw)
    const gain = ctx.createGain()
    gain.gain.value = this.micGain
    this.micGainNode = gain
    const dest = ctx.createMediaStreamDestination()
    try {
      const gate = await this._buildGateNode(ctx)
      this.micGateNode = gate
      source.connect(gate)
      gate.connect(gain)
    } catch {
      // Worklet indisponible : on garde l'audio (sans gate) plutôt que de couper.
      source.connect(gain)
    }
    gain.connect(dest)
    this.startAudioWatchdog()
    return dest.stream
  }

  /** Construit le noise gate « proximité » dans un AudioWorklet (thread audio).
   *
   *  Le traitement est dans `GATE_WORKLET_CODE`. Ici on charge le module,
   *  instancie le nœud, lui pousse le seuil courant et branche le retour de
   *  télémétrie (niveau + état) qui alimente le vumètre. Asynchrone car
   *  `addModule` doit résoudre avant de créer le nœud. Peut throw si le worklet
   *  est indisponible → l'appelant retombe alors sur un passage direct. */
  private async _buildGateNode(ctx: AudioContext): Promise<AudioWorkletNode> {
    await ctx.audioWorklet.addModule(gateWorkletUrl())
    const node = new AudioWorkletNode(ctx, 'mic-gate', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
    })
    node.port.postMessage({ openDb: this.micGateOpenDb })
    node.port.onmessage = (e: MessageEvent) => {
      const d = e.data as { db?: number; open?: boolean }
      if (typeof d?.db === 'number') this._micLevelDb = d.db
      if (typeof d?.open === 'boolean') this._gateOpen = d.open
    }
    return node
  }

  /** Libère le micro et le graphe audio (fin d'appel / échec). */
  /** Relance un AudioContext suspendu et trace l'incident. */
  private reviveAudioContext(ctx: AudioContext | null, label: string): void {
    if (!ctx || ctx.state !== 'suspended') return
    console.warn(`[PexiTalk SIP] AudioContext ${label} suspendu — relance`)
    void ctx.resume().catch((e) => {
      console.error(`[PexiTalk SIP] relance du graphe ${label} impossible`, e)
    })
  }

  /** Surveille les deux graphes WebAudio pendant toute la durée de l'appel.
   *
   *  `statechange` réagit immédiatement ; le balayage périodique rattrape les
   *  cas où l'évènement ne part pas (contextes « interrupted » de certaines
   *  plateformes, changement de périphérique par défaut). Sans ce filet, une
   *  suspension coupait le son jusqu'au raccrochage, sans erreur visible. */
  private startAudioWatchdog(): void {
    this.stopAudioWatchdog()
    this.micAudioCtx?.addEventListener('statechange', () => {
      this.reviveAudioContext(this.micAudioCtx, 'micro')
    })
    this.audioWatchdog = setInterval(() => {
      this.reviveAudioContext(this.micAudioCtx, 'micro')
      this.reviveAudioContext(this.remoteCtx, 'haut-parleur')
    }, SipController.AUDIO_WATCHDOG_MS)
  }

  private stopAudioWatchdog(): void {
    if (this.audioWatchdog) {
      clearInterval(this.audioWatchdog)
      this.audioWatchdog = null
    }
  }

  private teardownLocalStream(): void {
    this.stopAudioWatchdog()
    try { if (this.micGateNode) { this.micGateNode.port.onmessage = null; this.micGateNode.disconnect() } } catch { /* ignore */ }
    try { this.micRawStream?.getTracks().forEach((t) => t.stop()) } catch { /* ignore */ }
    try { void this.micAudioCtx?.close() } catch { /* ignore */ }
    this.micRawStream = null
    this.micAudioCtx = null
    this.micGainNode = null
    this.micGateNode = null
    this._micLevelDb = -99
    this._gateOpen = false
  }

  /** Volume du micro de l'agent : 0 = muet, 1 = normal, 2 = +6 dB (×2).
   *  Appliqué EN DIRECT si un appel est en cours, et mémorisé pour les
   *  appels suivants. */
  setMicGain(ratio: number): void {
    this.micGain = Math.max(0, Math.min(2, ratio))
    if (this.micGainNode) this.micGainNode.gain.value = this.micGain
    try { localStorage.setItem('pexitalk:micGain', String(this.micGain)) } catch { /* ignore */ }
  }

  /** Volume micro courant (0..2). */
  getMicGain(): number { return this.micGain }

  /** Seuil d'ouverture du noise gate, en dBFS (négatif). Appliqué EN DIRECT
   *  (le nœud relit `micGateOpenDb` à chaque bloc) et mémorisé pour les appels
   *  suivants. Plus haut = plus strict (coupe mieux les voix voisines). */
  setMicGateDb(db: number): void {
    this.micGateOpenDb = Math.max(-90, Math.min(-10, db))
    this.micGateNode?.port.postMessage({ openDb: this.micGateOpenDb })
    try { localStorage.setItem('pexitalk:micGateDb', String(this.micGateOpenDb)) } catch { /* ignore */ }
  }

  /** Seuil courant du noise gate (dBFS). */
  getMicGateDb(): number { return this.micGateOpenDb }

  /** Niveau RMS courant du micro en dBFS (pour le vumètre). -99 si inactif. */
  getMicLevelDb(): number { return this._micLevelDb }

  /** Le gate laisse-t-il passer le micro en ce moment ? (vumètre vert/gris). */
  isGateOpen(): boolean { return this._gateOpen }

  /** Un graphe micro tourne-t-il (appel en cours OU test) → le vumètre a des
   *  données à afficher. */
  isMicActive(): boolean { return this.micGateNode !== null }

  /** Ouvre le micro + le gate SANS appel, juste pour régler le seuil à l'oreille
   *  (vumètre). Le flux n'est envoyé nulle part (sink muet). Refusé si un appel
   *  utilise déjà le micro. Retourne true si le test a démarré. */
  async startMicTest(): Promise<boolean> {
    if (this.micAudioCtx) return false  // un appel mobilise déjà le micro
    try {
      const raw = await this._getMicStream()
      this.micRawStream = raw
      const Ctx = window.AudioContext
        || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new Ctx()
      this.micAudioCtx = ctx
      try { await ctx.resume() } catch { /* déjà actif */ }
      const source = ctx.createMediaStreamSource(raw)
      // Sink MUET : sert uniquement à faire tourner le graphe (donc le worklet
      // mesure) — rien n'est joué dans les haut-parleurs.
      const sink = ctx.createGain()
      sink.gain.value = 0
      const gate = await this._buildGateNode(ctx)
      this.micGateNode = gate
      source.connect(gate)
      gate.connect(sink)
      sink.connect(ctx.destination)
      this.micTestActive = true
      return true
    } catch {
      this.teardownLocalStream()
      return false
    }
  }

  /** Arrête le mode test micro et libère le graphe. Sans effet pendant un appel
   *  (le test n'est jamais actif à ce moment). */
  stopMicTest(): void {
    if (!this.micTestActive) return
    this.micTestActive = false
    this.teardownLocalStream()
  }

  hold(): void {
    if (!this.session) return
    this.session.hold()
    this.update({ ...this.current, status: 'on_hold', onHold: true })
  }

  unhold(): void {
    if (!this.session) return
    this.session.unhold()
    this.update({ ...this.current, status: 'in_call', onHold: false })
  }

  /**
   * Transfert aveugle (REFER) vers une extension/un numéro.
   *
   * JsSIP ne termine PAS la jambe de l'agent tout seul après un REFER : sans
   * gestion explicite, l'agent restait « collé » à un appel qu'il ne pouvait
   * plus raccrocher. On écoute donc le cycle du REFER :
   *  - `accepted` (le destinataire a décroché) → on raccroche la jambe de
   *    l'agent ; l'évènement `ended` enchaîne sur le post-appel normal.
   *  - `requestFailed` / `failed` → l'appel reste en ligne avec l'agent et on
   *    remonte une erreur (extension invalide, occupé, pas de réponse).
   *
   * Renvoie `true` si le REFER a pu être émis, `false` sinon.
   */
  transfer(target: string): boolean {
    if (!this.session) return false
    let referrer: unknown
    try {
      referrer = this.session.refer(`sip:${target}@${this.config.host}`, {
        eventHandlers: {
          accepted: () => {
            try { this.session?.terminate() } catch { /* déjà terminé */ }
          },
          requestFailed: (e: { cause?: string }) => {
            this.emitError({
              kind: 'call_failed',
              cause: e?.cause,
              message: "Transfert refusé — vérifiez l'extension du destinataire.",
            })
          },
          failed: (e: { cause?: string }) => {
            this.emitError({
              kind: 'call_failed',
              cause: e?.cause,
              message: 'Transfert échoué — destinataire injoignable ou occupé.',
            })
          },
        },
      })
    } catch (e: any) {
      this.emitError({
        kind: 'call_failed',
        cause: e?.message,
        message: 'Transfert impossible : cible invalide.',
      })
      return false
    }
    // refer() renvoie `false` si la session n'est pas dans un état transférable.
    if (referrer === false) {
      this.emitError({
        kind: 'call_failed',
        message: "Transfert impossible dans l'état actuel de l'appel.",
      })
      return false
    }
    return true
  }

  dtmf(tone: string): void {
    if (!this.session) return
    try {
      this.session.sendDTMF(tone)
    } catch (e) {
      console.warn('[PexiTalk SIP] dtmf failed', e)
    }
  }

  /** Retourne le MediaStream audio distant (voix du correspondant) ou null. */
  getRemoteStream(): MediaStream | null {
    if (!this.audioEl) return null
    return (this.audioEl.srcObject as MediaStream) ?? null
  }

  onCallChange(cb: CallChangeCallback): () => void {
    this.listeners.push(cb)
    return () => {
      this.listeners = this.listeners.filter((l) => l !== cb)
    }
  }

  private setupListeners(): void {
    if (!this.ua) return
    this.ua.on('newRTCSession', (data: NewRTCSessionEvent) => {
      const session = data.session
      const remoteUser = session.remote_identity?.uri?.user
      console.log(
        '[PexiTalk SIP] newRTCSession',
        session.direction,
        'from',
        remoteUser,
        'status',
        session.status,
      )
      if (session.direction === 'incoming') {
        this.session = session
        this.update({
          id: crypto.randomUUID(),
          remoteNumber: remoteUser || '',
          remoteName: session.remote_identity?.display_name || undefined,
          direction: 'inbound',
          startedAt: new Date(),
          durationSeconds: 0,
          status: 'ringing',
          muted: false,
          onHold: false,
        })
        // Un appel entrant était jusqu'ici SILENCIEUX : l'agent devait voir le
        // panneau pour s'en apercevoir. La sonnerie part sur le haut-parleur
        // choisi dans Réglages, au volume propre à l'agent.
        void this.ringtone.start(loadRingVolume(), loadOutputDeviceId())
        this.attachSessionListeners()
      }
    })
  }

  private attachSessionListeners(): void {
    if (!this.session) return

    // Préfère Opus + active le FEC sur NOTRE SDP (offre sortante ou réponse
    // entrante) → voix robuste aux pertes de paquets Internet. On ne touche
    // JAMAIS au SDP distant (`originator === 'remote'`). No-op si Asterisk n'a
    // pas proposé Opus (cf. preferOpusAudio).
    this.session.on('sdp', (e: SDPEvent) => {
      if (e.originator !== 'local' || typeof e.sdp !== 'string') return
      try { e.sdp = preferOpusAudio(e.sdp) }
      catch (err) { console.warn('[PexiTalk SIP] preferOpusAudio a échoué', err) }
    })

    const setupPcListeners = (pc: RTCPeerConnection) => {
      pc.addEventListener('track', (ev: RTCTrackEvent) => {
        console.log('[PexiTalk SIP] remote track received', ev.track.kind)
        this.attachRemoteStream()
      })
      pc.addEventListener('iceconnectionstatechange', () => {
        const st = pc.iceConnectionState
        console.log('[PexiTalk SIP] ICE state →', st)
        if (st === 'connected' || st === 'completed') {
          // Retour à la normale : on annule tout timer de grâce en attente et
          // on lève le flag de reprise (le son est revenu).
          this.clearIceGraceTimer()
          this.clearIceUnlockTimer()
          if (this.iceRecovering) {
            this.iceRecovering = false
            console.log('[PexiTalk SIP] ICE rétabli ✓')
          }
          this.attachRemoteStream()
        } else if (st === 'disconnected') {
          // Blip réseau : on laisse une fenêtre de grâce pour un rétablissement
          // spontané avant de forcer un ICE restart (évite de renégocier pour
          // rien sur un micro-décrochage d'une fraction de seconde).
          this.scheduleIceRestart(pc)
        } else if (st === 'failed') {
          // Échec franc : plus d'espoir de reprise spontanée → restart immédiat.
          this.clearIceGraceTimer()
          this.triggerIceRestart(pc)
        }
      })
      pc.addEventListener('connectionstatechange', () => {
        console.log('[PexiTalk SIP] PC state →', pc.connectionState)
        if (pc.connectionState === 'connected') {
          this.attachRemoteStream()
          // Force play — Chrome peut avoir suspendu l'audio context
          // pendant la phase ICE/DTLS (pas de data = silence = pause auto)
          if (this.audioEl && this.audioEl.paused) {
            this.audioEl.play().catch((e: Error) =>
              console.warn('[PexiTalk SIP] audio play retry', e),
            )
          }
          // Vérifier 2s après si des paquets RTP arrivent vraiment
          setTimeout(() => {
            pc.getStats().then((stats: RTCStatsReport) => {
              stats.forEach((report) => {
                const r = report as Record<string, unknown>
                if (r['type'] === 'inbound-rtp' && r['kind'] === 'audio') {
                  console.log('[PexiTalk SIP] RTP inbound — bytesReceived:', r['bytesReceived'], 'packetsReceived:', r['packetsReceived'], 'jitter:', r['jitter'])
                }
                if (r['type'] === 'outbound-rtp' && r['kind'] === 'audio') {
                  console.log('[PexiTalk SIP] RTP outbound — bytesSent:', r['bytesSent'], 'packetsSent:', r['packetsSent'])
                }
              })
            })
          }, 2000)
        }
      })
    }

    // peerconnection peut déjà être créé avant que notre listener soit attaché
    if (this.session.connection) {
      console.log('[PexiTalk SIP] peerconnection déjà prêt')
      setupPcListeners(this.session.connection)
    }

    this.session.on('peerconnection', (e: PeerConnectionEvent) => {
      console.log('[PexiTalk SIP] peerconnection ready')
      setupPcListeners(e.peerconnection)
    })
    // 18x (Ringing / Session Progress) sur un appel SORTANT : le distant
    // sonne. On affiche « Sonne… » et on joue la tonalité de retour locale.
    this.session.on('progress', (e: { originator: 'local' | 'remote' }) => {
      console.log('[PexiTalk SIP] session progress', e?.originator)
      if (this.current.direction === 'outbound') {
        if (this.current.status === 'connecting') {
          this.update({ ...this.current, status: 'ringing' })
        }
        this.startRingback()
      }
    })
    this.session.on('accepted', () => {
      console.log('[PexiTalk SIP] session accepted')
      this.stopLocalTones()
      this.update({ ...this.current, status: 'in_call', answeredAt: new Date() })
      this.attachRemoteStream()
    })
    this.session.on('confirmed', () => {
      console.log('[PexiTalk SIP] session confirmed')
      this.stopLocalTones()
      this.attachRemoteStream()
    })
    this.session.on('ended', (e: EndEvent) => {
      console.log('[PexiTalk SIP] session ended:', e?.cause)
      this.stopLocalTones()
      this.resetIceRecovery()
      this.teardownLocalStream()
      this.teardownRemoteProcessing()
      this.update({ ...this.current, status: 'ended', endedAt: new Date() })
      if (this.audioEl) this.audioEl.srcObject = null
    })
    this.session.on('failed', (e: EndEvent) => {
      console.error('[PexiTalk SIP] session failed:', e?.cause, e?.originator)
      this.stopLocalTones()
      this.resetIceRecovery()
      this.teardownLocalStream()
      this.teardownRemoteProcessing()
      // `failureCause` (cause SIP brute) propagée pour l'auto-classement des
      // appels non décrochés (occupé / sans réponse / invalide).
      this.update({
        ...this.current, status: 'ended', endedAt: new Date(),
        failureCause: e?.cause,
      })
      const causeMessages: Record<string, string> = {
        Unavailable: 'Pas de réponse',
        'Not Found': 'Numéro introuvable',
        Rejected: 'Appel refusé',
        Busy: 'Occupé',
        Canceled: 'Appel annulé',
        'Request Timeout': 'Pas de réponse',
        Forbidden: 'Accès refusé',
        'Address Incomplete': 'Numéro incomplet',
      }
      const userMessage = causeMessages[e?.cause ?? ''] ?? e?.cause ?? 'cause inconnue'
      this.emitError({
        kind: 'call_failed',
        cause: e?.cause,
        message: `Appel échoué : ${userMessage}`,
      })
    })
    // Erreurs spécifiques à la négociation média (getUserMedia, SDP, etc.)
    this.session.on('getusermediafailed', (err: unknown) => {
      console.error('[PexiTalk SIP] getUserMedia failed', err)
      this.emitError({
        kind: 'media_denied',
        message: "Le navigateur a refusé l'accès micro pendant l'appel.",
      })
    })
  }

  // ── Résilience ICE ─────────────────────────────────────────────────────────

  /** Annule le timer de grâce 'disconnected' (l'état est revenu à la normale
   *  ou une reprise a démarré). */
  private clearIceGraceTimer(): void {
    if (this.iceGraceTimer) {
      clearTimeout(this.iceGraceTimer)
      this.iceGraceTimer = null
    }
  }

  /**
   * Programme un ICE restart après la fenêtre de grâce si l'état est TOUJOURS
   * dégradé à l'échéance. Appelé sur 'disconnected' : un blip très court se
   * rétablit souvent tout seul, on ne renégocie donc pas immédiatement.
   * Idempotent : un second 'disconnected' ne réarme pas un timer déjà en cours.
   */
  private scheduleIceRestart(pc: RTCPeerConnection): void {
    if (this.iceGraceTimer) return  // déjà en attente de grâce
    this.iceGraceTimer = setTimeout(() => {
      this.iceGraceTimer = null
      const st = pc.iceConnectionState
      // Toujours dégradé après la grâce → on force la reprise. S'il est repassé
      // connected/completed entre-temps, le handler a déjà annulé ce timer.
      if (st === 'disconnected' || st === 'failed') {
        this.triggerIceRestart(pc)
      }
    }, SipController.ICE_GRACE_MS)
  }

  /**
   * Déclenche une reprise ICE : marque le restart sur la peerconnection
   * (`restartIce()` si dispo) PUIS redemande à JsSIP de ré-émettre une offre
   * avec `iceRestart` — l'offre régénérée embarque de nouveaux candidats ICE,
   * ce qui rétablit le chemin média sans couper l'appel.
   *
   * Anti-boucle : au plus ICE_MAX_ATTEMPTS tentatives dans une fenêtre
   * glissante, avec un petit back-off croissant. Au-delà, on cesse d'essayer et
   * on émet une info non fatale (l'appel n'est pas raccroché : le distant peut
   * encore raccrocher, ou le réseau revenir sur un futur évènement 'failed').
   */
  private triggerIceRestart(pc: RTCPeerConnection): void {
    if (!this.session) return
    // Une reprise (grâce + restart) est déjà en cours : on ne l'empile pas.
    if (this.iceRecovering || this.iceRestartTimer) return

    // Fenêtre glissante : purge les tentatives trop anciennes.
    const now = Date.now()
    this.iceRestartAttempts = this.iceRestartAttempts.filter(
      (t) => now - t < SipController.ICE_ATTEMPT_WINDOW_MS,
    )
    if (this.iceRestartAttempts.length >= SipController.ICE_MAX_ATTEMPTS) {
      console.warn('[PexiTalk SIP] ICE restart : trop de tentatives, abandon')
      this.emitError({
        kind: 'info',
        message: 'Connexion perdue — le réseau semble instable.',
      })
      return
    }

    this.iceRecovering = true
    const attemptIndex = this.iceRestartAttempts.length
    this.iceRestartAttempts.push(now)
    // Back-off léger : 0 ms, 500 ms, 1000 ms… pour ne pas marteler le serveur.
    const backoff = attemptIndex * 500

    console.log('[PexiTalk SIP] ICE restart programmé (tentative',
      attemptIndex + 1, 'back-off', backoff, 'ms)')
    // Feedback UI transitoire : « Reconnexion… » plutôt qu'un silence.
    this.emitError({ kind: 'info', message: 'Reconnexion…' })

    this.iceRestartTimer = setTimeout(() => {
      this.iceRestartTimer = null
      // La session a pu se terminer pendant le back-off.
      if (!this.session) { this.iceRecovering = false; return }
      // Marque le restart sur la PC (nouveaux candidats). Optionnel selon le
      // navigateur → optional chaining + garde de type.
      try { pc.restartIce?.() } catch { /* pas bloquant */ }
      let ok = false
      try {
        // renegotiate ré-émet une offre ; `iceRestart` force createOffer à
        // régénérer les candidats ICE (cf. JsSIP _createLocalDescription).
        ok = this.session.renegotiate({ rtcOfferConstraints: { iceRestart: true } })
      } catch (e) {
        console.warn('[PexiTalk SIP] renegotiate (ICE restart) a échoué', e)
        ok = false
      }
      if (!ok) {
        // Session pas dans un état ré-offrable (transfert en cours, ACK en
        // attente…) : on relâche le flag pour retenter sur un prochain
        // évènement ICE dégradé.
        console.warn('[PexiTalk SIP] renegotiate refusé (état non ré-offrable)')
        this.iceRecovering = false
        return
      }
      // Reprise partie : `iceRecovering` sera levé au retour d'ICE en
      // connected/completed. MAIS si elle reste sans effet, le drapeau
      // resterait à true pour tout le RESTE DE L'APPEL, et la garde en tête de
      // cette fonction bloquerait alors TOUTE nouvelle tentative : son mort,
      // appel toujours établi, aucune récupération possible sans raccrocher.
      // On le relâche donc après un délai, pour garder le droit de réessayer.
      this.iceUnlockTimer = setTimeout(() => {
        this.iceUnlockTimer = null
        if (this.iceRecovering) {
          console.warn('[PexiTalk SIP] reprise ICE sans effet — déverrouillage')
          this.iceRecovering = false
        }
      }, SipController.ICE_RECOVER_TIMEOUT_MS)
    }, backoff)
  }

  /** Remet à zéro tout l'état de reprise ICE (timers + compteur). Appelé à la
   *  fin/échec d'appel pour ne pas fuiter de timer entre deux appels. */
  private resetIceRecovery(): void {
    this.clearIceGraceTimer()
    if (this.iceRestartTimer) {
      clearTimeout(this.iceRestartTimer)
      this.iceRestartTimer = null
    }
    this.iceRestartAttempts = []
    this.clearIceUnlockTimer()
    this.iceRecovering = false
  }

  /** Annule le déverrouillage différé du verrou de reprise ICE. */
  private clearIceUnlockTimer(): void {
    if (this.iceUnlockTimer) {
      clearTimeout(this.iceUnlockTimer)
      this.iceUnlockTimer = null
    }
  }

  /**
   * Démarre la tonalité de retour d'appel (ringback) synthétisée localement.
   * Cadence française : ~1,5 s de tonalité (440 Hz) puis ~3,5 s de silence.
   * Idempotent — un second appel pendant que ça sonne ne crée pas 2 tonalités.
   */
  private startRingback(): void {
    if (this.ringbackCtx) return
    try {
      const Ctx = window.AudioContext
        || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new Ctx()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = 440
      gain.gain.value = 0.0001
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start()
      this.ringbackCtx = ctx
      this.ringbackOsc = osc
      this.ringbackGain = gain

      // Une « salve » = montée douce → 1,5 s de tonalité → descente douce.
      // Les rampes exponentielles évitent les clics. Répété toutes les 5 s.
      const burst = () => {
        if (!this.ringbackCtx || !this.ringbackGain) return
        const t = this.ringbackCtx.currentTime
        const g = this.ringbackGain.gain
        g.cancelScheduledValues(t)
        g.setValueAtTime(0.0001, t)
        g.exponentialRampToValueAtTime(0.16, t + 0.05)
        g.setValueAtTime(0.16, t + 1.5)
        g.exponentialRampToValueAtTime(0.0001, t + 1.55)
      }
      burst()
      this.ringbackTimer = setInterval(burst, 5000)
    } catch (e) {
      console.warn('[PexiTalk SIP] ringback indisponible', e)
    }
  }

  /** Coupe la tonalité de retour d'appel (décroché, fin, ou échec). */
  /** Coupe TOUTE tonalité locale : retour d'appel sortant et sonnerie
   *  entrante. Les deux ne coexistent jamais, couper les deux est donc
   *  toujours correct — et évite d'oublier un point de sortie. */
  private stopLocalTones(): void {
    this.stopRingback()
    this.ringtone.stop()
  }

  private stopRingback(): void {
    if (this.ringbackTimer) {
      clearInterval(this.ringbackTimer)
      this.ringbackTimer = null
    }
    try { this.ringbackOsc?.stop() } catch { /* déjà arrêté */ }
    try { void this.ringbackCtx?.close() } catch { /* déjà fermé */ }
    this.ringbackOsc = null
    this.ringbackGain = null
    this.ringbackCtx = null
  }

  private update(call: Call): void {
    this.current = call
    this.listeners.forEach((cb) => cb(call))
  }
}
